import { IconButton, Card, Typography, Stack, Avatar, Box, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDBadge from "components/MDBadge";
import DataTable from "examples/Tables/DataTable";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Visibility, Edit } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getBadge, setEditData, toggleOpenEditModal } from "../../../../redux/badge";
import ModalBadge from "../Modal/ModalBadge";
import ModalEditBadgeSize from "../Modal/ModalEditBadgeSize";
import LabelPreview from "../Preview/LabelPreview";

const columns = [
  { Header: "preview", accessor: "preview", width: "15%", align: "left" },
  { Header: "text", accessor: "text", width: "15%", align: "left" },
  { Header: "label", accessor: "label", width: "15%", align: "left" },
  { Header: "priority", accessor: "priority", align: "left" },
  { Header: "status", accessor: "status", align: "center" },
  { Header: "start date", accessor: "startDate", align: "center" },
  { Header: "end date", accessor: "endDate", align: "center" },
  { Header: "action", accessor: "action", align: "center" },
];

const TabBadge = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const shopDomain = useSelector((state) => state.shop.shopDomain);
  const badges = useSelector((state) => state.badge.list);
  const [active, setActive] = useState(false);
  const [badgeIndex, setBadgeIndex] = useState(0);

  useEffect(() => {
    setLoading(true);
    if (shopDomain) {
      dispatch(getBadge(shopDomain)).then((res) => setLoading(false));
    }
  }, [shopDomain]);

  const handleCloseModal = useCallback(() => {
    setActive(false);
  }, []);

  const handleActiveModal = useCallback(
    (index) => () => {
      setActive(true);
      setBadgeIndex(index);
    },
    []
  );

  const handleOpenEditModal = useCallback(
    (badge) => () => {
      dispatch(setEditData(badge));
      dispatch(toggleOpenEditModal());
    },
    []
  );

  const checkStatus = useCallback((conditions, status) => {
    if (!status) {
      return ["deactivate", "error"];
    }
    if (conditions?.starts_at && Date.now() - Date.parse(conditions.starts_at) < 0) {
      return ["scheduled", "warning"];
    }
    if (conditions?.ends_at && Date.now() - Date.parse(conditions.ends_at) > 0) {
      return ["expire", "secondary"];
    }
    return ["active", "success"];
  }, []);

  // Memo hóa rows/table để identity không đổi khi mở modal,
  // tránh react-table reset pageIndex về 0 (autoResetPage).
  const rows = useMemo(
    () =>
      badges.map(({ name, priority, status, conditions, text, design, badge_type }, index) => ({
        preview: (
          <div
            style={{ width: "50px", aspectRatio: "1/1", display: "flex", alignContent: "center" }}
          >
            <LabelPreview data={badges[index]} design={design} type="badge" />
          </div>
        ),
        text: (
          <Stack direction="row" spacing={2} alignItems="center">
            {badge_type === 1 && <Typography variant="caption">{text}</Typography>}
          </Stack>
        ),
        label: (
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography>{name}</Typography>
          </Stack>
        ),
        priority: (
          <Box>
            <MDBadge badgeContent={`${priority}`} color="success" variant="gradient" size="sm" />
          </Box>
        ),
        status: (
          <MDBox ml={-1}>
            <MDBadge
              badgeContent={checkStatus(conditions, status)[0]}
              color={checkStatus(conditions, status)[1]}
              variant="gradient"
              size="sm"
            />
          </MDBox>
        ),
        startDate: <Typography>{conditions.starts_at}</Typography>,
        endDate: <Typography>{conditions.ends_at}</Typography>,
        action: (
          <Stack direction="row" spacing={0.5}>
            <IconButton color="info" onClick={handleActiveModal(index)}>
              <Visibility />
            </IconButton>
            <IconButton color="warning" onClick={handleOpenEditModal(badges[index])}>
              <Edit />
            </IconButton>
          </Stack>
        ),
      })),
    [badges, checkStatus, handleActiveModal, handleOpenEditModal]
  );

  const tableData = useMemo(() => ({ columns, rows }), [rows]);

  return (
    <>
      {shopDomain && (
        <Card sx={{ marginTop: 3 }}>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <Typography variant="h6" color="whitesmoke">
              Badges
            </Typography>
          </MDBox>
          <MDBox pt={3}>
            {loading ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <CircularProgress color="info" />
              </Box>
            ) : badges.length === 0 ? (
              <Typography align="center">{`Don't have any badge`}</Typography>
            ) : (
              <DataTable
                table={tableData}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              ></DataTable>
            )}
          </MDBox>
          <ModalBadge active={active} onClose={handleCloseModal} dataIndex={badgeIndex} />
          <ModalEditBadgeSize />
        </Card>
      )}
    </>
  );
};

export default TabBadge;
